import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { IEcgRecordingSummary } from "../../../../@types/Ecg";
import { HorizontalDots } from "../../../../assets/icons/HorizontalDots";
import { CustomIconButton } from "../../../../components/CustomIconButton";
import { Table } from "../../../../components/Table";
import { Tag } from "../../../../components/Tag";
import { Toggle } from "../../../../components/Toggle";
import { useEcgRecordings } from "../../../../hooks/queries/ecg";
import { useModal } from "../../../../hooks/useModal";
import { useUserPreferences } from "../../../../hooks/useUserPreferences";
import { userService } from "../../../../services/userService";
import {
  getLongFormattedDate,
  getShortFormattedDate,
} from "../../../../utils/dateFormatter";
import {
  getEcgClassificationLabel,
  getEcgClassificationVariant,
} from "../../../../utils/ecg/ecgClassification";
import { EcgTracingModal } from "../EcgTracingModal";
import {
  DATE_FILTER_ALL,
  DATE_FILTER_CUSTOM,
  DateFilterOptionType,
} from "../MeasurementModal/components/CustomOptionsBar";
import { SubtitleConatiner } from "../MeasurementModal/components/MeasurementSection/MeasurementSection.styles";
import { ModalSkeleton } from "../MeasurementModal/components/ModalSkeleton";
import {
  HeaderContainer,
  Subtitle,
  Title,
} from "../MeasurementModal/MeasurementModal.styles";
import {
  DotsContainer,
  PinnedToogleContainer,
} from "../PinnedSection/PinnedSection.styles";
import { EcgOptionsBar } from "./EcgOptionsBar";
import { ecgResultsColumns } from "./ecgResultsColumns";
import {
  ModalContainer,
  OptionsContainer,
  TableContainer,
} from "./EcgResultsModal.styles";

export type EcgResultsModalProps = {
  patientId: number;
  // When rendered inside the patient page's Pinned section (rather than as a
  // modal), so the pin toggle unpins instead of closing a modal.
  isPinned?: boolean;
};

export function EcgResultsModal({
  patientId,
  isPinned = false,
}: EcgResultsModalProps) {
  const { t } = useTranslation();

  const { getDateRangeFilter, portalPreferences, changePortalPreferences } =
    useUserPreferences();
  const { closeModal } = useModal();
  const [selectedRecording, setSelectedRecording] =
    useState<IEcgRecordingSummary | null>(null);
  const [areOptionsOpen, setAreOptionsOpen] = useState(false);

  const optionsWrapperRef = useRef<HTMLDivElement>(null);
  const optionsButtonRef = useRef<HTMLDivElement>(null);

  const isEcgPinned = portalPreferences.pinnedMeasurementType === "ecg";

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        areOptionsOpen &&
        optionsWrapperRef.current &&
        !optionsWrapperRef.current.contains(event.target) &&
        optionsButtonRef.current &&
        !optionsButtonRef.current.contains(event.target)
      ) {
        setAreOptionsOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [areOptionsOpen]);

  const handlePinnedGraphChange = async (shouldPin: boolean) => {
    setAreOptionsOpen(false);

    const pinnedValue = shouldPin ? "ecg" : undefined;

    await userService.updatePinnedMeasurementType(pinnedValue);

    changePortalPreferences({
      ...portalPreferences,
      pinnedMeasurementType: pinnedValue,
    });

    // From the modal, pinning closes it so the table shows in the pinned slot;
    // from the pinned slot itself there is no modal to close.
    if (!isPinned) {
      closeModal();
    }
  };

  // Use the shared, global date range (same one the other graphs use), so the
  // selected interval is consistent across the portal.
  const dateRangeFilter = getDateRangeFilter();
  const selectedFilterDate: DateFilterOptionType = {
    id: dateRangeFilter.id,
    startDate: dateRangeFilter.startDate,
    endDate: dateRangeFilter.endDate,
  };

  // Changing the range here updates the global preference (and persists it),
  // exactly like the measurement graphs — so a change here applies everywhere.
  const handleFilterChange = (selected: DateFilterOptionType) => {
    const { id } = selected;

    changePortalPreferences({
      ...portalPreferences,
      dateRangeFilter: id,
      customFromDate:
        id === DATE_FILTER_CUSTOM ? selected.startDate : undefined,
      customToDate: id === DATE_FILTER_CUSTOM ? selected.endDate : undefined,
    });

    if (id !== DATE_FILTER_CUSTOM) {
      userService.updateDateRangeFilter(id);
    }
  };

  // "ALL" sends no bounds (fetch everything); any other range is sent to the
  // API so it filters server-side and the query refetches on change.
  const isAll = selectedFilterDate.id === DATE_FILTER_ALL;
  const fromDate = isAll
    ? undefined
    : selectedFilterDate.startDate.format("YYYY-MM-DD");
  const toDate = isAll
    ? undefined
    : selectedFilterDate.endDate.format("YYYY-MM-DD");

  const { recordings, isForbidden, isFetching, isRefetching } =
    useEcgRecordings(patientId, fromDate, toDate);

  // Any fetch (initial or a range change) shows the full skeleton, matching the
  // measurement modals.
  const isLoading = isFetching || isRefetching;

  // Subtitle = the active range. Always shown (even with no data), matching the
  // measurement graphs. For "ALL" with data use the recordings' own span so it
  // doesn't show an arbitrary 1900 start.
  const subtitle = (() => {
    const start =
      isAll && recordings.length > 0
        ? dayjs(recordings[recordings.length - 1].recordedAt)
        : selectedFilterDate.startDate;

    return `${getShortFormattedDate(
      start.toISOString()
    )} - ${getShortFormattedDate(selectedFilterDate.endDate.toISOString())}`;
  })();

  const tableData = useMemo(
    () =>
      recordings.map((recording) => ({
        id: recording.id,
        date: getLongFormattedDate(recording.recordedAt, "-"),
        duration: `${recording.durationSeconds}s`,
        leads: `${recording.leadCount}`,
        heartRate:
          recording.averageHeartRateBpm != null
            ? `${recording.averageHeartRateBpm} bpm`
            : "-",
        result: (
          <Tag
            variant={getEcgClassificationVariant(recording.classification)}
            clickable={false}
          >
            {getEcgClassificationLabel(recording.classification)}
          </Tag>
        ),
        recording,
      })),
    [recordings]
  );

  const handleRowClick = (row: { recording: IEcgRecordingSummary }) => {
    setSelectedRecording(row.recording);
  };

  const emptyStateText = isForbidden
    ? t("patientPage_ecg_noAccess")
    : t("patientPage_ecg_none");

  // While fetching (initial load or a range change) show the same skeleton as
  // the measurement modals. Rendered without the ModalContainer padding (the
  // skeleton carries its own margins) so it lines up with the title/ranges.
  if (isLoading) {
    return <ModalSkeleton />;
  }

  return (
    <ModalContainer>
      <HeaderContainer style={{ marginBottom: 0 }}>
        <Title>{t("patientPage_ecg_recordings")}</Title>
        <Subtitle>{subtitle}</Subtitle>

        <SubtitleConatiner>
          <EcgOptionsBar
            selectedFilterDate={selectedFilterDate}
            onChange={handleFilterChange}
          />
        </SubtitleConatiner>
      </HeaderContainer>

      <TableContainer>
        <Table
          hasBorder={false}
          data={tableData}
          columns={ecgResultsColumns}
          onRowClick={handleRowClick}
          selectedId={selectedRecording?.id}
          emptyStateText={emptyStateText}
        />
      </TableContainer>

      <EcgTracingModal
        summary={selectedRecording}
        open={!!selectedRecording}
        onClose={() => setSelectedRecording(null)}
      />

      <OptionsContainer pinned={isPinned}>
        <DotsContainer
          onClick={() => setAreOptionsOpen((prevState) => !prevState)}
          ref={optionsButtonRef}
        >
          <CustomIconButton
            style={{ position: "absolute", top: "-5px", left: "-25px" }}
          >
            <HorizontalDots />
          </CustomIconButton>
        </DotsContainer>

        {areOptionsOpen ? (
          <PinnedToogleContainer ref={optionsWrapperRef}>
            <span>{t("patientPage_ecg_pinGraph")}</span>
            <Toggle
              checked={isEcgPinned}
              onCheckedChange={handlePinnedGraphChange}
            />
          </PinnedToogleContainer>
        ) : null}
      </OptionsContainer>
    </ModalContainer>
  );
}
